import React, { useEffect, useState } from "react";
import { OrderPaymentTransactionResponse } from "@/api";

import PaymentTransactionsTable from "./PaymentTransactionTable";
import { PaymentTransactionService } from "./service/orderPaymentsService";

export interface PaymentTransactionsSectionProps {
  orderId: number;

  onEdit?: (
    transaction: OrderPaymentTransactionResponse
  ) => void;
}

export default function PaymentTransactionsSection({
  orderId,
  onEdit,
}: PaymentTransactionsSectionProps) {
  const [transactions, setTransactions] =
    useState<OrderPaymentTransactionResponse[]>([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadTransactions = async () => {
      if (!orderId) {
        setTransactions([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result =
          await PaymentTransactionService.list(orderId);

        if (cancelled) {
          return;
        }

        setTransactions(result ?? []);
      } catch (error) {
        console.error(
          "❌ Failed to load payment transactions:",
          error
        );

        if (cancelled) {
          return;
        }

        setTransactions([]);
        setError(
          "Unable to load payment transactions."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadTransactions();

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return (
    <section className="space-y-4">
      {/* =====================================================
          HEADER
          ===================================================== */}

      <div>
        <h2 className="text-lg font-semibold">
          Payment Transactions
        </h2>

        <p className="text-sm text-gray-500">
          Payment transactions recorded against this order.
        </p>
      </div>

      {/* =====================================================
          CONTENT
          ===================================================== */}

      {loading ? (
        <div className="rounded-md border px-4 py-6 text-center text-sm text-gray-500">
          Loading payment transactions...
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-600">
          {error}
        </div>
      ) : (
        <PaymentTransactionsTable
          transactions={transactions}
          onEdit={onEdit}
        />
      )}
    </section>
  );
}